import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

interface SubscriptionInfo {
  id: string;
  status: 'pending' | 'active' | 'cancelled' | 'completed' | 'failed';
  customerEmail: string;
  planName: string;
  priceIls: number;
  cancelledAt: string | null;
}

function fmtIls(amountMinor: number): string {
  return `₪ ${(amountMinor / 100).toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

export function SubscriptionCancel(): JSX.Element {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [info, setInfo] = useState<SubscriptionInfo | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setErr('no_token');
      return;
    }
    fetch(`/api/subscription/by-token/${encodeURIComponent(token)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`http_${r.status}`))))
      .then((d: SubscriptionInfo) => setInfo(d))
      .catch((e: Error) => setErr(e.message));
  }, [token]);

  const onCancel = async (): Promise<void> => {
    setBusy(true);
    setErr(null);
    try {
      const res = await fetch('/api/subscription/cancel', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      if (!res.ok) {
        const d = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new Error(d?.error ?? `http_${res.status}`);
      }
      setDone(true);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--bg)',
        display: 'grid',
        placeItems: 'center',
        padding: 20,
      }}
    >
      <div
        className="card"
        style={{ padding: 28, width: '100%', maxWidth: 480, display: 'grid', gap: 14 }}
      >
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>Отмена подписки</h1>

        {err && (
          <div
            style={{
              padding: '10px 12px',
              background: 'var(--danger-subtle-bg)',
              color: 'var(--danger-fg)',
              borderRadius: 'var(--radius)',
              fontSize: 13,
            }}
          >
            {err === 'no_token' && 'Не передан токен подписки.'}
            {err === 'http_404' && 'Подписка не найдена. Возможно, ссылка устарела.'}
            {(err === 'provider_error' || err === 'provider_rejected' || err === 'cancel_not_confirmed') &&
              'Не удалось отменить подписку: платёжная система не подтвердила отмену. Подписка пока активна — попробуйте ещё раз позже или напишите нам.'}
            {!['no_token', 'http_404', 'provider_error', 'provider_rejected', 'cancel_not_confirmed'].includes(err) &&
              `Ошибка: ${err}`}
          </div>
        )}

        {info && !done && (
          <>
            <div style={{ fontSize: 14, color: 'var(--fg-muted)', lineHeight: 1.55 }}>
              Подписка <b><bdi>{info.planName}</bdi></b> для <b>{info.customerEmail}</b>.
              <br />
              Сумма: {fmtIls(info.priceIls)} в месяц.
            </div>
            {info.status === 'cancelled' || info.status === 'completed' ? (
              <div
                style={{
                  padding: '10px 12px',
                  background: 'var(--bg-subtle)',
                  borderRadius: 'var(--radius)',
                  fontSize: 13,
                  color: 'var(--fg-muted)',
                }}
              >
                Подписка уже {info.status === 'cancelled' ? 'отменена' : 'завершена'}.
                {info.cancelledAt && ` (${new Date(info.cancelledAt).toLocaleString('ru-RU')})`}
              </div>
            ) : info.status === 'pending' ? (
              <div
                style={{
                  padding: '10px 12px',
                  background: 'var(--bg-subtle)',
                  borderRadius: 'var(--radius)',
                  fontSize: 13,
                  color: 'var(--fg-muted)',
                }}
              >
                Подписка ещё не активирована — отменить пока нельзя.
              </div>
            ) : (
              <>
                <div style={{ fontSize: 13, color: 'var(--fg-subtle)', lineHeight: 1.5 }}>
                  После отмены автосписания прекратятся. Если хотите вернуться — оформите подписку заново.
                </div>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => void onCancel()}
                  disabled={busy}
                >
                  {busy ? 'Отменяем…' : 'Отменить подписку'}
                </button>
              </>
            )}
          </>
        )}

        {done && (
          <div
            style={{
              padding: '10px 12px',
              background: 'var(--ok-subtle-bg, #e6f9ef)',
              color: 'var(--ok-fg, #0a6b3a)',
              borderRadius: 'var(--radius)',
              fontSize: 13,
            }}
          >
            Подписка отменена. Списания больше не будут происходить.
          </div>
        )}
      </div>
    </div>
  );
}
